import { FC, useState } from "react";
import { Input } from "antd";
import cn from "classnames";
import { useTranslation } from "react-i18next";
import { track } from "@amplitude/analytics-browser";
import { MdGridView } from "react-icons/md";
import Carrier from "src/components/Carrier/Carrier";
import { FiltersIcon, SearchIcon } from "src/assets/Icons";
import { CorrectIcon } from "@Icons/MainIcons";

type CategoriesFieldMobProps = {
  value?: string;
  onChange?: (value: string) => void;
  options: { label: string; value: string }[];
  isFetching: boolean;
};

const AVATAR_COLORS = [
  "bg-red-10 text-red-70",
  "bg-grey-10 text-grey-80",
  "bg-green-10 text-green-90",
  "bg-orange-10 text-orange-80",
  "bg-blue-10 text-blue-70",
  "bg-yellow-10 text-yellow-90",
  "bg-dark-red-10 text-dark-red-60",
];

export const getAvatarColor =(key: string) => {
  const sum = [...key].reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return AVATAR_COLORS[sum % AVATAR_COLORS.length];
};

const CategoriesFieldMob: FC<CategoriesFieldMobProps> = ({ value, onChange, options, isFetching }) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");

  const filteredOptions = options.filter((option) => option.label.toLowerCase().includes(search.trim().toLowerCase()));

  return (
    <Carrier
      label={t("Giftcards.filterCategories")}
      renderTrigger={({ open }) => (
        <div onClick={open} className="bg-background-default! flex h-13 w-13 items-center justify-center rounded-md shadow-sm">
          <FiltersIcon />
        </div>
      )}
      renderSheet={({ close }) => (
        <div className="flex min-h-0 flex-1 flex-col gap-3 p-5">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("Giftcards.searchCategories")}
            prefix={<SearchIcon className="text-font-subtle me-1 h-4 w-4" />}
            className="bg-background-minimal! border-none! shadow-none! rounded-md! py-3.5! ps-5! pe-4!"
            allowClear
          />
          <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto">
            {isFetching ? (
              Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 p-3">
                  <div className="bg-background-subtle h-9 w-9 shrink-0 animate-pulse rounded-sm" />
                  <div className="bg-background-subtle h-4 w-1/2 animate-pulse rounded-sm" />
                </div>
              ))
            ) : filteredOptions.length ? (
              filteredOptions.map((option) => {
                const isSelected = option.value === (value ?? "");
                const isAll = option.value === "";
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      track("Category Selected", { ...option });
                      onChange?.(option.value);
                      close();
                    }}
                    className={cn(
                      "flex w-full cursor-pointer items-center gap-3 rounded-md p-3 text-start transition-colors",
                      isSelected ? "bg-background-hover" : "hover:bg-background-hover",
                    )}
                  >
                    <div
                      className={cn(
                        "flex h-9 w-9 shrink-0 items-center justify-center rounded-sm text-sm font-semibold uppercase",
                        isAll ? "bg-background-primary text-white" : getAvatarColor(option.value),
                      )}
                    >
                      {isAll ? <MdGridView className="h-4 w-4" /> : option.label.charAt(0)}
                    </div>
                    <span className={cn("text-font-default flex-1 truncate text-sm capitalize", isSelected ? "font-semibold" : "font-medium")}>
                      {option.label}
                    </span>
                    {isSelected ? (
                      <CorrectIcon className="text-font-active h-5 w-5 shrink-0" />
                    ) : (
                      <div className="border-border-default h-5 w-5 shrink-0 rounded-full border-2" />
                    )}
                  </button>
                );
              })
            ) : (
              <div className="text-font-subtle py-6 text-center text-sm">{t("Giftcards.noCategoriesFound")}</div>
            )}
          </div>
        </div>
      )}
    />
  );
};

export default CategoriesFieldMob;
